import { Global, css } from '@emotion/react';
import {
  ActionIcon,
  AspectRatio,
  Flex,
  Icon,
  Skeleton,
  Stack,
  Tooltip,
  useShowNotification,
} from '@inflearn/ds-react';
import { useLang } from '@inflearn/i18n-tools';
import { copyCertificateLink, useCertificateDownload } from '@inflearn/modals';
import { faDownToLine, faShareNodes } from '@inflearn/pro-regular-svg-icons';
import { every } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import type { GetCertificateDetailResponse } from '../../../../resources/@types/courseSchema';
import { logger } from '../../../../resources/Logger';
import { useClientApiCourseCertificatesDetailQuery } from '../../../queries/useClientApiCourseCertificatesDetailQuery';

interface Props {
  certificateResponse: GetCertificateDetailResponse;
}

export const Certificate = ({ certificateResponse }: Props) => {
  const router = useRouter();
  const { lang } = useLang();

  const { generateImage, downloadPdf } = useCertificateDownload(lang);
  const [img, setImg] = useState<'ImageUrl' | null>(null);
  const params = getCertificateQueries(router.query.id as string);
  const { t } = useTranslation();

  const { showNotification } = useShowNotification();

  const { data, isSuccess } = useClientApiCourseCertificatesDetailQuery({
    query: {
      ...params,
      lang,
    },
    options: {
      enabled: isValidQuery(params),
      initialData: certificateResponse,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  });

  useEffect(() => {
    if (isSuccess && data && !img) {
      try {
        (async () => {
          const certificateImage = await generateImage(
            {
              courseTitle: data.title,
              instructorName: data.instructorName,
              realName: data.realName,
              runtime: data.runtime,
              startedAt: data.startedAt,
              certificatedAt: data.certificatedAt,
            },
            params,
          );
          setImg(certificateImage);
        })();
      } catch (error) {
        logger.error(error);
      }
    }
  }, [isSuccess, data, img, lang]);

  const handleLinkCopy = () => {
    copyCertificateLink(params)
      .then(() => {
        showNotification({
          type: 'success',
          title: t('링크가 복사되었습니다.'),
        });
      })
      .catch(() => {
        showNotification({
          type: 'error',
          title: t('링크 복사에 실패했습니다. 반복될 경우 고객센터에 문의해주세요.'),
        });
      });
  };

  const handlePdfDownload = async () => {
    if (!img) {
      return;
    }

    await downloadPdf(img);
  };

  return (
    <Stack>
      <Global styles={fontFamilies} />
      {img ? (
        <>
          <Flex mt={32} mb={16} justify="flex-end" gap={16}>
            <Tooltip withArrow label={t('링크 복사')} position="top" onClick={handleLinkCopy}>
              <ActionIcon size="xl" aria-label={t('수료증 링크 복사하기')} variant={'default'}>
                <Icon icon={faShareNodes} />
              </ActionIcon>
            </Tooltip>

            <Tooltip withArrow label={t('PDF 다운로드')} position="top" onClick={handlePdfDownload}>
              <ActionIcon
                size="xl"
                aria-label={t('수료증 이미지 다운로드하기')}
                variant={'default'}
              >
                <Icon icon={faDownToLine} />
              </ActionIcon>
            </Tooltip>
          </Flex>
          <AspectRatio ratio={CERT_RATIO}>
            <img src={img} />
          </AspectRatio>
        </>
      ) : (
        <AspectRatio ratio={CERT_RATIO} mt={64}>
          <Skeleton w={'100%'} h={'100%'} />
        </AspectRatio>
      )}
    </Stack>
  );
};

const CERT_RATIO = 877 / 559;

export const getCertificateQueries = (certificateParams: string) => {
  const [userId, courseId, voucherId] = (certificateParams || '').split('-');
  return {
    userId: Number(userId),
    courseId: Number(courseId),
    voucherId: Number(voucherId),
  };
};

const isValidQuery = (query: ReturnType<typeof getCertificateQueries>) =>
  every(query, (item) => !Number.isNaN(item));

function cdnUrl(url: string) {
  return `${process.env.NEXT_PUBLIC_CDN_URL}/font${url}`;
}

const fontFamilies = css`
@font-face {
  font-family: 'NanumMyeongjoExtraBold';
  font-weight: 800;
  src: local('NanumMyeongjoExtraBold'),
    url(${cdnUrl('/NanumMyeongjoExtraBold.woff2')}) format('woff2'),
    url(${cdnUrl('/NanumMyeongjoExtraBold.woff')}) format('woff'),
    url(${cdnUrl('/NanumMyeongjoExtraBold.ttf')}) format('truetype');
}`;
